import moment from 'moment';
import { useMemo, useState } from 'react';
import { ChevronLeftIcon } from '../../icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../../icons/ChevronRightIcon';
import {
  DayCell,
  Grid,
  Header,
  NavButton,
  NavControls,
  RangeLabel,
  WeekdayCell,
  Wrapper,
} from './DayStripSelector.styles';
import type { DayStripSelectorProps } from './DayStripSelector.types';

function getDefaultWeekdays(): string[] {
  const wd = moment.localeData().weekdaysMin();
  return [...wd.slice(1), wd[0]].map((d) => d.charAt(0).toUpperCase());
}

export function DayStripSelector({
  dates,
  selectedDate,
  onDateSelect,
  rangeLabel,
  labels,
}: DayStripSelectorProps) {
  const weekdays = labels?.weekdays ?? getDefaultWeekdays();
  const previousWeekLabel = labels?.previousWeek ?? 'Previous week';
  const nextWeekLabel = labels?.nextWeek ?? 'Next week';

  const availableDates = useMemo(() => [...new Set(dates)].sort(), [dates]);

  const weekStarts = useMemo(() => {
    if (availableDates.length === 0) return [];
    const first = moment(availableDates[0]).startOf('isoWeek');
    const last = moment(availableDates[availableDates.length - 1]).startOf('isoWeek');
    const starts: string[] = [];
    const cursor = first.clone();
    while (cursor.isSameOrBefore(last)) {
      starts.push(cursor.format('YYYY-MM-DD'));
      cursor.add(1, 'week');
    }
    return starts;
  }, [availableDates]);

  const [weekIndex, setWeekIndex] = useState(() => {
    const target = selectedDate ?? availableDates[0];
    if (!target) return 0;
    const targetWeek = moment(target).startOf('isoWeek').format('YYYY-MM-DD');
    const index = weekStarts.indexOf(targetWeek);
    return index >= 0 ? index : 0;
  });

  if (availableDates.length === 0) return null;

  const availableSet = new Set(availableDates);
  const safeIndex = Math.min(weekIndex, weekStarts.length - 1);
  const weekStart = moment(weekStarts[safeIndex]);
  const days = Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, 'day'));

  return (
    <Wrapper>
      <Header>
        {rangeLabel && <RangeLabel>{rangeLabel}</RangeLabel>}
        <NavControls>
          <NavButton
            type="button"
            aria-label={previousWeekLabel}
            disabled={safeIndex === 0}
            onClick={() => setWeekIndex(Math.max(safeIndex - 1, 0))}
          >
            <ChevronLeftIcon />
          </NavButton>
          <NavButton
            type="button"
            aria-label={nextWeekLabel}
            disabled={safeIndex >= weekStarts.length - 1}
            onClick={() => setWeekIndex(Math.min(safeIndex + 1, weekStarts.length - 1))}
          >
            <ChevronRightIcon />
          </NavButton>
        </NavControls>
      </Header>
      <Grid>
        {weekdays.map((weekday, index) => (
          <WeekdayCell key={index} aria-hidden>
            {weekday}
          </WeekdayCell>
        ))}
        {days.map((day) => {
          const iso = day.format('YYYY-MM-DD');
          const isAvailable = availableSet.has(iso);
          const isSelected = iso === selectedDate;
          return (
            <DayCell
              key={iso}
              type="button"
              aria-label={day.format('dddd, D MMMM YYYY')}
              aria-pressed={isSelected}
              disabled={!isAvailable}
              {...(isSelected ? { 'data-selected': '' } : undefined)}
              onClick={() => onDateSelect(iso)}
            >
              {day.date()}
            </DayCell>
          );
        })}
      </Grid>
    </Wrapper>
  );
}
